/**
 * [TEMP: legacy-search back-compat] TEMPORARY legacy hash-URL shim.
 *
 * The CSR search path encodes all search state in the URL *hash*, e.g.
 * `/search#q=trees&cf-ec_category=3D&nf-ec_price_filter=0.01..20`. The SSR
 * engine reads state from the *query string* instead — the hash never reaches
 * the server, so it can't seed `fetchStaticState`. While CSR and SSR run side
 * by side under the `as-search-ssr` roll-out, existing hash-based deep links
 * and bookmarks must keep restoring state on the SSR path.
 *
 * {@link migrateLegacyCoveoHash} migrates the whole hash into the query string
 * in ONE `history.replaceState` on first render (no browser redirect — a hard
 * redirect reloads mid-interaction and a soft one leaves the hash fighting the
 * sync), consuming the legacy entry so back/forward isn't polluted. One-shot
 * rather than gated on the state→URL write catching up: params the engine
 * can't round-trip back into the query string (`cf-*`, `tab`, ...) would keep
 * such a gate open forever, leaving the hash in place and every subsequent
 * history write on `replace` — killing the Back button for the session.
 *
 * The two encodings share most Coveo parameter names, but values need
 * translation:
 * - `cf-{field}` (hierarchical category path, comma-joined) becomes the flat
 *   `f-{field}` with ` > `-joined segments — the only category facet the SSR
 *   engine registers (same translation as the CSR path's translateCfFragment).
 * - multi-values (facet values, ranges) are comma-joined in the hash but
 *   repeated keys in the query string; sanitizeSearchParams then keeps only
 *   the first where the SSR engine restores a single value (the CSR category
 *   facet is multi-select, the SSR tree single-select).
 * - scalars and single values pass through, filtered by the same allowlist as
 *   server-side seeding (sanitizeSearchParams) — hash params the engine can't
 *   or must not restore are dropped rather than parked in the query string.
 *
 * (The `@lib/coveo/parameters` legacy map is unrelated: it translates the
 * older ASV2 `category=/price=/orderBy=` scheme used by the `/listing`
 * middleware redirect, not this Coveo-native hash. The hash detection and
 * parsing primitives live in `@lib/coveo/legacyHash`, dependency-free, because
 * CSR-bundled components share them.)
 *
 * TODO: Remove this file and its call sites in SearchParameterManager with
 * Task 13 (`specs/coveo-ssr/13-chore-decommission-csr-search.md`), once the CSR
 * (hash-based) search is decommissioned — the SSR path is query-param-native
 * and needs none of this.
 */

import {
  isLegacyCoveoParam,
  readLegacyHashSegments,
  safeDecode,
} from '@lib/coveo/legacyHash';
import { dispatchCoveoUrlChange } from '@lib/coveo/urlChangeEvent';
import { isEngineSearchParam, sanitizeSearchParams } from './searchParams';

/** Prefixes whose hash values comma-join multiple selections/ranges. */
const MULTI_VALUE_FACET_PREFIX = /^(f|fExcluded|af|nf|df|sf)-.+/;

function toQueryEntries(
  key: string,
  rawValue: string
): Array<[string, string]> {
  if (key.startsWith('cf-')) {
    const flatValue = rawValue
      .split(',')
      .map((segment) => safeDecode(segment).trim())
      .join(' > ');
    return [[`f-${key.slice(3)}`, flatValue]];
  }
  if (MULTI_VALUE_FACET_PREFIX.test(key)) {
    return rawValue.split(',').map((value) => [key, safeDecode(value)]);
  }
  return [[key, safeDecode(rawValue)]];
}

/**
 * Migrate a v1 Coveo hash into the query string in one `history.replaceState`:
 * translate the hash's Coveo params (see the module doc), replace the query
 * string's engine-readable Coveo params with them, and drop the whole Coveo
 * hash so it can't shadow the query. The hash is authoritative for the entire
 * engine keyspace (`numberOfResults` excepted — see the module doc), not
 * merely winning per key: the CSR engine serializes its
 * COMPLETE state into the hash on every interaction while preserving the query
 * string verbatim (useUrlManagerSync), so after a degraded or control-branch
 * CSR session the query string carries the stale pre-session seed — a key the
 * hash no longer has was removed by the user, and merging key-by-key would
 * resurrect it on the next SSR load. A hash the fallback seeded and the user
 * never moved past translates back to the values the SSR already seeded, so
 * the hydrated engine's URL synchronize still no-ops on the common reload.
 * `numberOfResults` is the one exception to hash authority: the CSR engine
 * never serializes it into the hash (Coveo's UrlManager excludes it — see
 * useUrlManagerSync), so its absence carries no removal signal and the
 * query-string value survives unless the hash supplies a valid replacement.
 * Non-Coveo query params and non-Coveo fragments (e.g. `#main`) are preserved,
 * and a URL with no Coveo hash is left untouched. Idempotent: after the first
 * call there is no Coveo hash left to migrate.
 *
 * Must run before anything captures the URL (useAppHistoryRouter, the engine
 * sync effects), so the caller invokes it during render — all render passes
 * complete before any effect runs.
 */
export function migrateLegacyCoveoHash(): void {
  const segments = readLegacyHashSegments();
  const coveoSegments = segments.filter(({ key }) => isLegacyCoveoParam(key));
  if (coveoSegments.length === 0) {
    return;
  }

  const translated = new URLSearchParams();
  coveoSegments.forEach(({ key, rawValue }) => {
    toQueryEntries(key, rawValue).forEach(([k, v]) => translated.append(k, v));
  });

  const sanitized = sanitizeSearchParams(translated);
  const merged = new URLSearchParams(window.location.search);
  Array.from(new Set(merged.keys()))
    .filter(
      (key) =>
        isEngineSearchParam(key) &&
        (key !== 'numberOfResults' || sanitized.has('numberOfResults'))
    )
    .forEach((key) => merged.delete(key));
  sanitized.forEach((value, key) => merged.append(key, value));

  const { pathname } = window.location;
  const search = merged.toString();
  const hash = segments
    .filter(({ key }) => !isLegacyCoveoParam(key))
    .map(({ raw }) => raw)
    .join('&');
  window.history.replaceState(
    null,
    document.title,
    `${pathname}${search ? `?${search}` : ''}${hash ? `#${hash}` : ''}`
  );
  // Native replaceState fires no event; subscribers outside the engine (e.g.
  // SearchResultsBanner) must learn the sale filter moved out of the hash.
  // Deferred: this runs during render, and a synchronous dispatch would let a
  // listener setState while another component is rendering — and would re-fire
  // on a discarded concurrent render pass.
  queueMicrotask(dispatchCoveoUrlChange);
}
